import { Platform } from 'react-native'

// Utilidades para abrir/descargar los documentos generados del check-in.
//
// Los documentos se guardan en Neon como dataURL `data:text/html;base64,...`.
// Chrome bloquea la navegacion top-level a `data:text/html`, asi que para abrirlos
// los decodificamos a un Blob y abrimos su object URL (que si esta permitido).

function decodeHtmlFromDataUrl(url: string): string | null {
  const match = /^data:text\/html;base64,([\s\S]*)$/.exec(url)
  if (!match) return null
  const base64 = match[1]
  try {
    if (typeof atob === 'function') {
      const bin = atob(base64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
      return new TextDecoder('utf-8').decode(bytes)
    }
    if (typeof Buffer !== 'undefined') return Buffer.from(base64, 'base64').toString('utf8')
  } catch (e) {
    console.debug('decodeHtmlFromDataUrl fallo', e)
  }
  return null
}

/** Abre un string HTML en una pestana nueva via Blob (no se bloquea como data:text/html). */
export function openHtmlInNewTab(html: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const objectUrl = URL.createObjectURL(blob)
    window.open(objectUrl, '_blank')
    // No revocamos de inmediato para que la pestana cargue.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
  } catch (e) {
    console.debug('openHtmlInNewTab fallo', e)
  }
}

/**
 * Abre un documento guardado del check-in. Acepta URLs servibles normales y los
 * dataURL `data:text/html;base64,...` de los documentos generados (F244, certificado).
 */
export function openStoredDocument(url?: string | null): void {
  if (!url || Platform.OS !== 'web' || typeof window === 'undefined') return
  const html = decodeHtmlFromDataUrl(url)
  if (html != null) {
    openHtmlInNewTab(html)
    return
  }
  window.open(url, '_blank')
}

/**
 * Descarga un documento guardado del check-in. Si es un data:text/html;base64 (F244,
 * certificado) lo descarga como .html; si es otra URL la abre en pestaña nueva.
 */
export function downloadDocument(url?: string | null, filename = 'documento'): void {
  if (!url || Platform.OS !== 'web' || typeof window === 'undefined') return
  const html = decodeHtmlFromDataUrl(url)
  if (html == null) { window.open(url, '_blank'); return }
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename.endsWith('.html') ? filename : `${filename}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
  } catch (e) {
    console.debug('downloadDocument fallo', e)
    openHtmlInNewTab(html)
  }
}

/** Descarga un objeto como fichero .json en el navegador. */
export function downloadJson(filename: string, data: unknown): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return
  try {
    const json = JSON.stringify(data, null, 2)
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename.endsWith('.json') ? filename : `${filename}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
  } catch (e) {
    console.debug('downloadJson fallo', e)
    // Fallback: abrir el JSON en una pestana.
    try {
      window.open('data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(data, null, 2)), '_blank')
    } catch { /* noop */ }
  }
}
